import React from 'react';
import { motion } from 'framer-motion';
import { 
  Users, Calendar, FileText, Shield, Bell, TrendingUp, 
  CheckCircle2, Star, ArrowRight, Building2, Heart, 
  Clock, Lock, BarChart3, Sparkles
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { SEO } from '@/components/SEO';

const ClinicianSales = () => {
  const navigate = useNavigate();

  const { data: tiers } = useQuery({
    queryKey: ['clinician-subscription-tiers'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('clinician_subscription_tiers')
        .select('*')
        .eq('is_active', true)
        .order('sort_order', { ascending: true });
      
      if (error) throw error;
      return data;
    }
  });

  const features = [
    {
      icon: Calendar,
      title: 'Interactive Reflection Calendar',
      description: 'See your day, week, and month at a glance. Click any client to view their full session history.',
      highlight: true
    },
    {
      icon: Users,
      title: 'Client Organisation',
      description: 'Track attendance, cancellations, DNAs, and payments. Export session data for invoicing with date range selection.'
    },
    {
      icon: FileText,
      title: 'Session Notes & Observations',
      description: 'Add ongoing observations and session notes. Access client notes directly from your calendar.'
    },
    {
      icon: Shield,
      title: 'GDPR Compliant Data Sharing',
      description: 'Clients control exactly what data they share. Granular consent management ensures full compliance.'
    },
    {
      icon: Bell,
      title: 'Smart Notifications',
      description: 'Get notified when clients complete check-ins, reach milestones, or need attention.'
    },
    {
      icon: BarChart3,
      title: 'Client Progress Insights',
      description: 'View mood trends, therapy progress, and AI-generated summaries with client consent.'
    },
    {
      icon: Clock,
      title: 'Cancellation Policy Tracking',
      description: 'Set your cancellation policy and automatically track costs from late cancellations and DNAs.'
    },
    {
      icon: TrendingUp,
      title: 'Practice Analytics',
      description: 'Understand your practice better with session statistics and client engagement metrics.'
    }
  ];

  const clientBenefits = [
    'Deeper collaborative relationships through shared insights',
    'Continuity of care between sessions',
    'Progress tracking they can see and celebrate',
    'Secure, confidential data sharing they control',
    'Better prepared sessions with pre-shared reflections',
    'Collaborative goal setting and achievement tracking'
  ];

  const whyTherappi = [
    {
      title: 'Support Informed Care',
      description: 'Access client check-ins and mood data between sessions to provide more informed, personalised care.',
      icon: Heart
    },
    {
      title: 'Save Administrative Time',
      description: 'Export session records for invoicing, track payments, and manage your calendar all in one place.',
      icon: Clock
    },
    {
      title: 'Build Stronger Relationships',
      description: 'Clients feel supported between sessions, knowing their therapist can see their progress.',
      icon: Users
    },
    {
      title: 'Stay Compliant',
      description: 'GDPR-compliant data sharing with granular client consent. You only see what clients choose to share.',
      icon: Lock
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-background via-background/95 to-muted/30">
      <SEO
        title="Therappi® for Therapists - Practice Platform & Client Insights"
        description="Streamline your practice with Therappi®: client insights, secure notes, scheduling and outcome tracking - built by a Chartered Counselling Psychologist."
        path="/for-therapists"
      />
      {/* Hero Section */}
      <section className="relative py-20 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-therapeutic opacity-10" />
        <div className="max-w-6xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <Badge className="mb-4 bg-primary/10 text-primary border-primary/20">
              <Building2 className="w-3 h-3 mr-1" />
              For Therapists & Clinicians
            </Badge>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-medium text-foreground mb-6">
              Transform Your Practice with{' '}
              <span className="text-primary">Therappi</span>
            </h1>
            
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
              A comprehensive clinician portal that enhances client relationships, 
              streamlines administration, and provides deeper insights into your clients' 
              wellbeing between sessions.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                className="bg-primary hover:bg-primary/90"
                onClick={() => navigate('/auth?clinician=true')}
              >
                Start Free Trial
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                onClick={() => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' })}
              >
                View Pricing
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-medium text-foreground mb-4">
              Everything You Need to Run Your Practice
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Powerful tools designed specifically for therapists, counsellors, and mental health professionals.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className={`h-full border-border/50 ${feature.highlight ? 'ring-2 ring-primary/20' : ''}`}>
                  <CardContent className="p-6">
                    <feature.icon className="w-10 h-10 text-primary mb-4" />
                    <h3 className="font-medium text-foreground mb-2">{feature.title}</h3>
                    <p className="text-sm text-muted-foreground">{feature.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Therappi */}
      <section className="py-16 px-4 bg-muted/30">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-medium text-foreground mb-4">
              Why Clinicians Choose Therappi
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {whyTherappi.map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="flex gap-4"
              >
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                    <item.icon className="w-6 h-6 text-primary" />
                  </div>
                </div>
                <div>
                  <h3 className="font-medium text-foreground mb-2">{item.title}</h3>
                  <p className="text-muted-foreground">{item.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Client Benefits */}
      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <Badge className="mb-4 bg-accent/10 text-accent border-accent/20">
                <Heart className="w-3 h-3 mr-1" />
                Client Experience
              </Badge>
              <h2 className="text-3xl font-medium text-foreground mb-6">
                What Your Clients Get
              </h2>
              <p className="text-muted-foreground mb-8">
                When clients choose to connect with you on Therappi, they unlock a deeper, 
                more collaborative reflective experience. It's entirely their choice what to share.
              </p>
              <div className="space-y-3">
                {clientBenefits.map((benefit, index) => (
                  <motion.div
                    key={benefit}
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-center gap-3"
                  >
                    <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0" />
                    <span className="text-foreground">{benefit}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <Card className="border-border/50 overflow-hidden">
                <CardContent className="p-8 bg-gradient-to-br from-primary/5 to-accent/5">
                  <div className="text-center">
                    <Sparkles className="w-16 h-16 text-primary mx-auto mb-4" />
                    <h3 className="text-xl font-medium text-foreground mb-2">
                      Collaborative Support
                    </h3>
                    <p className="text-muted-foreground">
                      "Since connecting with my therapist on Therappi, I feel more supported
                      between sessions."
                    </p>
                    <p className="text-sm text-muted-foreground mt-4">- Illustrative user feedback</p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-16 px-4 bg-muted/30">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-medium text-foreground mb-4">
              Simple, Transparent Pricing
            </h2>
            <p className="text-muted-foreground">
              Choose the plan that fits your practice. All plans include a 14-day free trial.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {tiers?.map((tier, index) => (
              <motion.div
                key={tier.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className={`h-full ${tier.slug === 'professional' ? 'ring-2 ring-primary' : 'border-border/50'}`}>
                  <CardContent className="p-6">
                    {tier.slug === 'professional' && (
                      <Badge className="mb-4 bg-primary text-primary-foreground">
                        <Star className="w-3 h-3 mr-1" />
                        Most Popular
                      </Badge>
                    )}
                    <h3 className="text-xl font-medium text-foreground mb-2">{tier.name}</h3>
                    <div className="mb-4">
                      {tier.is_enterprise ? (
                        <p className="text-2xl font-medium text-foreground">{tier.price_description}</p>
                      ) : (
                        <>
                          <span className="text-3xl font-medium text-foreground">
                            £{tier.price_gbp}
                          </span>
                          <span className="text-muted-foreground">/month</span>
                        </>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground mb-4">
                      {tier.max_clients ? `Up to ${tier.max_clients} clients` : 'Unlimited clients'}
                    </p>
                    <ul className="space-y-2 mb-6">
                      {tier.features?.map((feature: string) => (
                        <li key={feature} className="flex items-start gap-2 text-sm">
                          <CheckCircle2 className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                          <span className="text-muted-foreground">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <Button 
                      className={`w-full ${tier.slug === 'professional' ? 'bg-primary hover:bg-primary/90' : ''}`}
                      variant={tier.slug === 'professional' ? 'default' : 'outline'}
                      onClick={() => tier.is_enterprise 
                        ? window.location.href = 'mailto:hello@therappi.app?subject=Enterprise Enquiry'
                        : navigate('/auth?clinician=true')
                      }
                    >
                      {tier.is_enterprise ? 'Contact Us' : 'Start Free Trial'}
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-medium text-foreground mb-6">
              Ready to Transform Your Practice?
            </h2>
            <p className="text-xl text-muted-foreground mb-8">
              Join hundreds of therapists already using Therappi to enhance their client relationships 
              and streamline their practice.
            </p>
            <Button 
              size="lg" 
              className="bg-primary hover:bg-primary/90"
              onClick={() => navigate('/auth?clinician=true')}
            >
              Start Your Free Trial Today
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ClinicianSales;
